import React, { useRef } from 'react';
import FeedbackTab from '~/components/feedback/FeedbackTab';
import { useCommonAppContext } from '~/shared/contexts/CommonAppContext';
import { useIsAtOrNarrowerThan } from '~/shared/utils/useBreakpoint';
import { getLcn } from '~/utils/labeled-classnames';
import useActivityReportingOnLocationChange from '~/utils/useActivityReportingOnLocationChange';
import RootErrorBoundary from '../components/RootErrorBoundary';
import SignInFlowDialog from '../session/SignInFlowDialog';
import SuspendedArea from './SuspendedArea';
import SuspendedOutlet from './SuspendedOutlet';
import isProduction from '~/utils/isProduction';
import ViteRefreshCert from './ViteRefreshCert';
import StandardSidebar from './StandardSidebar';

// first step towards making this js based, rather than hard-coded
// values in css below are hard-coded but should change
export const sidebarWidth = 300;

export default function StandardLayout({
  banner,
  sideBarContent,
  sidebarBanner,
  pageUsesSidebar = true,
  footer,
  useBgColor = true,
  bodyColTop,
  feedbackDialogContent,
  bannerIsSticky = true,
  alwaysFloatSidebar = false,
}: {
  banner: React.ReactElement
  bannerIsSticky?: boolean
  sideBarContent?: React.ReactElement
  sidebarBanner?: React.ReactElement
  pageUsesSidebar?: boolean
  footer?: React.ReactElement
  useBgColor?: boolean
  bodyColTop?: React.ReactElement
  feedbackDialogContent?: React.ReactElement
  alwaysFloatSidebar?: boolean
}) {
  const isXs = useIsAtOrNarrowerThan('xs')
  const isSmOrNarrower = useIsAtOrNarrowerThan('sm')
  const { sideBarOpen } = useCommonAppContext();
  const bannerContainerRef = useRef<HTMLDivElement>(null);
  const footerContainerRef = useRef<HTMLElement>(null);
  const heightOfBanner = bannerContainerRef.current?.offsetHeight || 0;
  const heightOfFooter = footerContainerRef.current?.offsetHeight || 0;
  const heightOfFooterAndBanner = heightOfBanner + heightOfFooter

  useActivityReportingOnLocationChange()

  const styles = {
    appBody: {
      display: 'flex',
      // helps reduce CLS, and size appBody
      minHeight: `calc(100dvh - ${heightOfFooterAndBanner}px)`,
      alignItems: 'top',
    },
    bodyCol: {
      flexGrow: 1,
      borderRadius: '30px 0 0 0'
    },
  }

  const lcn = getLcn({
    StandardLayout: '',
    appBody: [
      'relative',
      isSmOrNarrower ? '' : 'z-0',
    ],
    bodyCol: [
      'z-10',
      'min-w-0', // allows child divs to have overflow-x
    ],
    bodyColInner: [
      'p-3 sm:p-4 ',
      'h-full', // or vertical centering won't work
    ],
    footerContainer: '',
    bannerContainer: [
      bannerIsSticky ? 'sticky top-0 z-20' : '',
    ]
  });

  const { sessionUser, hasFetchedSessionUser } = useCommonAppContext()


  return (
    <>
      <div className={lcn.StandardLayout}>

        <div className={lcn.bannerContainer} ref={bannerContainerRef}>
          <SuspendedArea>
            {banner}
          </SuspendedArea>
        </div>

        <div className={lcn.appBody} style={styles.appBody}>

          <StandardSidebar
            sideBarContent={sideBarContent}
            sidebarBanner={sidebarBanner}
            pageUsesSidebar={pageUsesSidebar}
            useBgColor={useBgColor}
            heightOfBanner={heightOfBanner}
            heightOfFooterAndBanner={heightOfFooterAndBanner}
            alwaysFloatSidebar={alwaysFloatSidebar}
          />


          <main className={lcn.bodyCol} style={styles.bodyCol}>
            {bodyColTop}
            <div className={lcn.bodyColInner}>
              <SuspendedOutlet />
              <RootErrorBoundary />
            </div>
          </main>
        </div >

        {!isXs && feedbackDialogContent &&
          <FeedbackTab>
            {feedbackDialogContent}
          </FeedbackTab>
        }

        {footer &&
          <footer className={lcn.footerContainer} ref={footerContainerRef}>
            <SuspendedArea>
              {footer}
            </SuspendedArea>
          </footer>
        }
      </div >

      <SignInFlowDialog />

      {
        !isProduction &&
        <ViteRefreshCert />
      }
    </>
  );
}; 